<template>
  <div v-loading="loading" class="order-detail-container">
    <!-- 红人全案标志 -->
    <div v-if="isRedPersonFullCase.isRedPersonFullCase" class="red-person-banner">
      <div class="banner-content">
        <span class="banner-icon">🎯</span>
        <span class="banner-text">{{ isRedPersonFullCase.displayText }}</span>
        <span class="banner-subtext">此订单为红人全案合作形式，包含直播需求和分成信息</span>
      </div>
    </div>

    <div class="content">
      <!-- 订单信息卡片 -->
      <div v-if="orderInfo.length > 0" class="info-card">
        <div class="card-title">订单信息</div>
        <div class="info-grid">
          <div class="info-item" v-for="(item, index) in orderInfo" :key="`order_${item.field}_${index}`">
            <div class="info-label">{{ item.title }}</div>
            <div class="info-value" :class="getValueClass(item.value)">{{ item.value }}</div>
          </div>
        </div>
      </div>

      <!-- 直播需求卡片（仅红人全案显示） -->
      <div v-if="isRedPersonFullCase.isRedPersonFullCase && liveRequirementInfo.hasData" class="info-card">
        <div class="card-title">直播需求</div>
        <div class="info-grid">
          <div class="info-item" v-if="liveRequirementInfo.money_or_times">
            <div class="info-label">保量/保次</div>
            <div class="info-value">{{ liveRequirementInfo.money_or_times }}</div>
          </div>
          <div class="info-item" v-if="liveRequirementInfo.live_count">
            <div class="info-label">要求直播次数</div>
            <div class="info-value">{{ liveRequirementInfo.live_count }}</div>
          </div>
          <div class="info-item" v-if="liveRequirementInfo.gq_amount">
            <div class="info-label">保量金额</div>
            <div class="info-value">{{ liveRequirementInfo.gq_amount }}</div>
          </div>
        </div>
      </div>
      <!-- 分成信息卡片（仅红人全案显示） -->
      <live-data-schedule class="info-card" v-if="isRedPersonFullCase.isRedPersonFullCase" :order-id="order_id"
        style="margin-top: 20px;" />

      <!-- 字段映射信息卡片（调试用） -->
      <div v-if="Object.keys(fieldMapping).length > 0" class="info-card">
        <div class="card-title">字段映射信息</div>
        <div class="info-grid">
          <div class="info-item" v-for="(mapping, title) in fieldMapping" :key="title">
            <div class="info-label">{{ title }}</div>
            <div class="info-value">
              field: {{ mapping.field || '无' }}<br>
              name: {{ mapping.name || '无' }}<br>
              type: {{ mapping.type || 'string' }}<br>
              display: {{ mapping.display ? '是' : '否' }}
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { getTableColsAll } from "@/api/cache";
import { getOrderDetailBase } from "@/api/order";
import { liveGuaranteeTheOrderSchedule } from "@/api/liveGuarantee";
import LiveDataSchedule from "@/views/adOrder/project/components/LiveDataSchedule.vue";

export default {
  name: 'AdOrderDetail',
  components: { LiveDataSchedule },
  data() {
    return {
      order_no: null,
      order_id: null,
      loading: false,
      formData: {},
      tableSettings: {}, // 存储表格设置数据
      shareData: [], // 分成信息数据
      fieldMapping: {}, // 存储字段标题到field的映射
    }
  },
  computed: {
    // 判断是否是红人全案 - 返回对象包含更多信息
    isRedPersonFullCase() {
      // 直接从formData中查找合作形式的值
      if (!this.formData || typeof this.formData !== 'object') {
        return {
          isRedPersonFullCase: false,
          cooperationForm: '',
          foundInFormData: false,
          foundInTableSettings: false,
          displayText: '未知合作形式'
        }
      }

      // 尝试多种可能的字段名
      const possibleKeys = ['合作形式', 'form_of_cooperation', 'cooperation_form', 'cooperationForm']
      let cooperationFormValue = ''
      let foundInFormData = false

      for (const key of possibleKeys) {
        if (this.formData[key] === '红人全案') {
          cooperationFormValue = '红人全案'
          foundInFormData = true
          break
        }
      }

      // 如果直接查找不到，尝试通过表格设置查找
      if (!foundInFormData) {
        cooperationFormValue = this.getFieldValue('合作形式')
      }

      const isRedPerson = cooperationFormValue === '红人全案'

      return {
        isRedPersonFullCase: isRedPerson,
        cooperationForm: cooperationFormValue,
        foundInFormData: foundInFormData,
        foundInTableSettings: !foundInFormData && cooperationFormValue !== '',
        displayText: isRedPerson ? '红人全案订单' : `其他合作形式: ${cooperationFormValue || '未知'}`
      }
    },

    // 订单信息字段（红人全案显示）
    orderInfo() {
      if (!this.formData || !this.tableSettings || typeof this.tableSettings !== 'object') {
        return []
      }

      // 红人全案的订单信息字段列表
      const orderFields = [
        '订单编号',
        '创建时间',
        '品牌',
        '行业',
        '产地',
        '产品',
        '订单来源',
        '客单',
        '品类',
        '合作形式',
        '执行时间',
        '红人名称'
      ]

      return this.getFieldsByTitles(orderFields)
    },

    // 直播需求信息（仅红人全案显示） - 返回对象
    liveRequirementInfo() {
      if (!this.isRedPersonFullCase.isRedPersonFullCase) {
        return {
          hasData: false,
          money_or_times: '',
          live_count: '',
          gq_amount: ''
        }
      }

      // 获取字段值
      const moneyOrTimes = this.formData.money_or_times || ''
      const liveCount = this.formData.live_count || ''
      const gqAmount = this.formData.gq_amount || ''

      // 转换money_or_times值为中文
      let moneyOrTimesText = ''
      if (moneyOrTimes === 'money') {
        moneyOrTimesText = '保量'
      } else if (moneyOrTimes === 'times') {
        moneyOrTimesText = '保次'
      } else if (moneyOrTimes === 'none') {
        moneyOrTimesText = '不保'
      } else {
        moneyOrTimesText = moneyOrTimes || '-'
      }

      // 格式化金额
      const gqAmountFormatted = gqAmount ? this.formatMoney(gqAmount) : '-'

      return {
        hasData: moneyOrTimes || liveCount || gqAmount,
        money_or_times: moneyOrTimesText,
        live_count: liveCount || '-',
        gq_amount: gqAmountFormatted
      }
    },

    // 分成信息（仅红人全案显示，从接口获取）
    shareInfo() {
      if (!this.isRedPersonFullCase.isRedPersonFullCase || !this.shareData || this.shareData.length === 0) {
        return []
      }

      // 分成信息字段列表
      const shareFields = [
        '核算归属月份',
        '平台',
        '保量进度',
        '可结算收入',
        '分成比例',
        '分成金额'
      ]

      // 如果有分成数据，显示第一条数据
      if (this.shareData.length > 0) {
        const firstItem = this.shareData[0]
        return shareFields.map(field => {
          let value = firstItem[field] || firstItem[this.getFieldKey(field)] || '-'

          // 格式化处理
          if (value === '-') {
            return { title: field, value: '-', field: field, type: 'string' }
          }

          // 根据字段类型格式化
          if (field.includes('金额') || field.includes('收入') || field.includes('进度')) {
            value = this.formatMoney(value)
          } else if (field.includes('比例')) {
            value = typeof value === 'number' ? `${value}%` : value
          } else if (field.includes('月份') || field.includes('日期')) {
            value = this.formatDate(value)
          }

          return { title: field, value, field: field, type: 'string' }
        })
      }

      return []
    }
  },
  created() {
    this.order_no = this.$route.params.order_no
    this.order_id = this.$route.params.order_id
    this.loadOrderDetail()
  },
  methods: {
    async loadOrderDetail() {
      this.loading = true
      try {
        // 并行请求订单详情和表格设置
        const [tableSettingsResult] = await Promise.all([
          getTableColsAll({ type: 'ad_order' })
        ])

        let { info } = await getOrderDetailBase({ id: this.order_id }).then((res) => {
          return res.model;
        })

        this.formData = { ...info } || {}
        if (this.formData.is_quantity === 'Y') {
          this.formData.money_or_times = 'money'
        } else if (this.formData.is_quantity_times === 'Y') {
          this.formData.money_or_times = 'times'
        } else {
          this.formData.money_or_times = 'none'
        }
        this.tableSettings = tableSettingsResult.model.tableCols || {}

        // 提取字段映射
        this.extractFieldMapping()

        // 如果是红人全案，请求分成信息接口
        if (this.isRedPersonFullCase.isRedPersonFullCase) {
          await this.loadShareInfo()
        }
      } finally {
        this.loading = false
      }
    },

    // 提取字段标题到field的映射
    extractFieldMapping() {
      const fieldTitles = [
        '订单编号',
        '创建时间',
        '品牌',
        '行业',
        '产地',
        '产品',
        '订单来源',
        '客单',
        '品类',
        '合作形式',
        '执行时间',
        '红人名称',
        '经纪人',
        '要求发布日期',
        '下单日期',
        '发布平台',
        '内容形式',
        '含税收入',
        '返点金额',
        '代下单平台费',
        '粉丝福利',
        '授权价',
        '投流费',
        '其他费',
        '支付方式',
        '是否挂车',
        '挂车佣金（%）',
        '购物车费用',
        '线下结佣',
        '是否直播',
        '直播佣金',
        '直播坑位费',
        '整单业绩金额',
        '实际所得',
        '分成比例',
        '红人所得'
      ]

      const mapping = {}

      // 遍历表格设置，找到每个标题对应的field
      Object.values(this.tableSettings).forEach(setting => {
        const field = setting.field
        const name = setting.name
        const title = setting.title || field || name

        // 如果标题在指定的列表中，记录映射
        if (fieldTitles.includes(title)) {
          mapping[title] = {
            field: field,
            name: name,
            type: setting.type || 'string',
            display: setting.display || false
          }
        }
      })

      this.fieldMapping = mapping
    },

    async loadShareInfo() {
      try {
        // 使用导入的API函数
        const response = await liveGuaranteeTheOrderSchedule({
          order_id: this.order_id
        })

        this.shareData = response.model || []
      } catch (error) {
        // console.error('加载分成信息失败:', error)
        this.shareData = []
      }
    },

    // 根据标题获取字段值
    getFieldsByTitles(titles) {
      const result = []

      // 遍历表格设置，找到需要显示的字段
      Object.values(this.tableSettings).forEach(setting => {
        // 检查是否显示
        if (setting.display !== true) {
          return
        }

        const field = setting.field
        const name = setting.name
        const title = setting.title || field || name
        const type = setting.type || 'string'

        // 只保留指定的字段
        if (!titles.includes(title)) {
          return
        }

        const value = this.getFieldValueBySetting(setting)
        result.push({
          title,
          value,
          field: field,
          name,
          type,
          found: value !== '-'
        })
      })

      // 按指定顺序排序
      const sortedResult = []
      titles.forEach(fieldTitle => {
        const item = result.find(item => item.title === fieldTitle)
        if (item) {
          sortedResult.push(item)
        }
      })

      return sortedResult
    },

    // 根据设置获取字段值
    getFieldValueBySetting(setting) {
      const field = setting.field
      const name = setting.name
      // const title = setting.title || field || name
      const type = setting.type || 'string'

      // 获取订单数据中对应的值，先尝试field，再尝试name
      let value = undefined

      // 辅助函数：通过路径获取嵌套对象的值
      const getValueByPath = (obj, path) => {
        if (!obj || !path) return undefined
        const keys = path.split('.')
        let current = obj
        for (const key of keys) {
          if (current && typeof current === 'object' && key in current) {
            current = current[key]
          } else {
            return undefined
          }
        }
        return current
      }

      // 尝试多种方式获取值
      if (field) {
        // 尝试直接访问
        if (this.formData.hasOwnProperty(field)) {
          value = this.formData[field]
        }
        // 尝试嵌套路径访问
        else if (field.includes('.')) {
          value = getValueByPath(this.formData, field)
        }
      }

      // 如果field没找到，尝试name
      if (value === undefined && name) {
        if (this.formData.hasOwnProperty(name)) {
          value = this.formData[name]
        } else if (name.includes('.')) {
          value = getValueByPath(this.formData, name)
        }
      }

      // 处理不同类型的值
      if (value === null || value === undefined || value === '') {
        value = '-'
      } else {
        // 根据类型进行格式化
        switch (type) {
          case 'number':
          case 'money':
            value = this.formatMoney(value)
            break
          case 'date':
          case 'datetime':
            value = this.formatDate(value)
            break
          case 'boolean':
          case 'bool':
            value = value ? '是' : '否'
            break
          default:
            // 字符串类型直接显示，但特殊处理Y/N值
            // eslint-disable-next-line no-case-declarations
            const strValue = String(value).trim().toUpperCase()
            if (strValue === 'Y' || strValue === 'YES' || strValue === 'TRUE' || strValue === '1') {
              value = '是'
            } else if (strValue === 'N' || strValue === 'NO' || strValue === 'FALSE' || strValue === '0') {
              value = '否'
            } else {
              value = String(value)
            }
        }
      }

      return value
    },

    // 根据字段key获取值
    getFieldValueByKey(fieldKey) {
      // 直接尝试从formData中获取
      if (this.formData.hasOwnProperty(fieldKey)) {
        return this.formData[fieldKey]
      }

      // 尝试通过表格设置查找
      const setting = Object.values(this.tableSettings).find(s => {
        return s.field === fieldKey || s.name === fieldKey
      })

      if (setting) {
        return this.getFieldValueBySetting(setting)
      }

      return undefined
    },

    // 根据字段名获取值
    getFieldValue(fieldName) {
      // 先在表格设置中查找对应的字段
      const setting = Object.values(this.tableSettings).find(s => {
        const title = s.title || s.field || s.name
        return title === fieldName
      })

      if (setting) {
        return this.getFieldValueBySetting(setting)
      }

      return '-'
    },

    // 根据字段标题获取对应的key
    getFieldKey(fieldTitle) {
      const setting = Object.values(this.tableSettings).find(s => {
        const title = s.title || s.field || s.name
        return title === fieldTitle
      })

      return setting ? (setting.field || setting.name) : fieldTitle
    },

    formatMoney(value) {
      if (!value && value !== 0) return '-'
      return this.$utils.numberFormat(value, 2)
    },
    formatDate(date) {
      if (!date) return '-'
      return this.$utils.parseTime(date, '{y}-{m}-{d} {h}:{i}')
    },
    getValueClass(value) {
      if (value === '是') {
        return 'value-yes'
      } else if (value === '否') {
        return 'value-no'
      } else if (value === '-') {
        return 'value-empty'
      }
      return ''
    },
  }
}
</script>

<style scoped>
.order-detail-container {
  padding: 16px;
  background-color: #f5f5f5;
  min-height: 100vh;
  width: 100%;
}

/* 红人全案标志样式 */
.red-person-banner {
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
  border-left: 6px solid #ff4757;
  animation: fadeIn 0.5s ease-in-out;
}

.banner-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.banner-icon {
  font-size: 24px;
  animation: pulse 2s infinite;
}

.banner-text {
  font-size: 18px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

.banner-subtext {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
  min-width: 200px;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

.content {
  width: 100%;
  margin: 0;
}

/* 卡片标题样式 - 参考Edit.vue */
.card-title {
  position: relative;
  padding-left: 40px;
  margin-bottom: 12px;
  color: rgba(50, 50, 50, 1);
  line-height: 20px;
  font-size: 14px;
  font-weight: bold;
  text-align: left;
}

.card-title::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: calc(40px - 8px - 6px);
  width: 8px;
  height: 8px;
  background: rgba(224, 63, 61, 1);
  border-radius: 2px;
}

/* 信息卡片样式 - 满屏紧凑 */
.info-card {
  padding: 16px;
  border-radius: 6px;
  border: 1px solid rgba(221, 221, 221, 1);
  background: #fff;
  width: 100%;
  margin: 0 0 16px 0;
}

/* 网格布局 - 更紧凑 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

/* 信息项样式 - 更紧凑 */
.info-item {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
}

.info-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 2px;
  font-weight: 500;
  line-height: 1.3;
}

.info-value {
  font-size: 13px;
  color: #333;
  line-height: 1.4;
  word-break: break-word;
  min-height: 18px;
}

/* 特殊值样式 */
.info-value.value-yes {
  color: #67c23a;
  font-weight: 500;
}

.info-value.value-no {
  color: #f56c6c;
  font-weight: 500;
}

.info-value.value-empty {
  color: #c0c4cc;
}

/* 无数据状态 */
.no-data {
  text-align: center;
  padding: 40px;
  color: #909399;
  font-size: 14px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid rgba(221, 221, 221, 1);
  width: 100%;
  margin: 0;
}

/* 响应式调整 - 更紧凑 */
@media (max-width: 768px) {
  .order-detail-container {
    padding: 12px;
  }

  .info-card,
  .no-data {
    padding: 12px;
    margin-bottom: 12px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .card-title {
    padding-left: 32px;
    margin-bottom: 10px;
  }

  .card-title::after {
    left: calc(32px - 8px - 6px);
    bottom: 4px;
  }

  .info-label {
    font-size: 11px;
  }

  .info-value {
    font-size: 12px;
  }
}

/* 大屏幕优化 */
@media (min-width: 1200px) {
  .info-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 10px;
  }
}

@media (min-width: 1600px) {
  .info-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
  }
}
</style>

<style lang="scss">
.order-detail-container {
  .el-tabs__content {
    padding-top: 15px;
  }

  .el-table {
    margin-top: 10px;
  }

  .el-card__header {
    padding: 12px 20px;
    background-color: #f9f9f9;
    border-bottom: 1px solid #ebeef5;
  }

  .el-card__body {
    padding: 20px;
  }
}
</style>
